import queryString from "query-string";
import { HOSTS } from "rylak-common";
import { responseAndErrorHandling } from "rylak-common/src/service/useServiceHook";

const defaultHeaders = { "Content-Type": "application/json" };

// Special init call for Server Side Rendering. Experimental solution
export const fetchInitSSR = async ({ queryKey }) => {
    const brandName = queryKey[1];
    const host = HOSTS[queryKey[2]]?.baseUrl;
    const includeReturnReasons = queryKey[3];

    const res = fetch(`${host}/api/v1/digitalreturn/session/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            brandName: brandName,
            includeReturnReasons: includeReturnReasons,
        }),
    })
        .then((response) => {
            return { response: response };
        })
        .catch((error) => {
            return { error: error };
        })
        .then(({ response, error }) => {
            return responseAndErrorHandling({ response, error });
        });
    return res;
};

export function fetchLogin(system, { email, orderId, ...rest }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/login`, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify({
            buyerEmail: email,
            salesPlatformOrderId: orderId,
            ...rest,
        }),
    });
}

export function fetchSubmitProductReturns(system, { sessionId, ...rest }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/return`, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify({ ...rest }),
    });
}

export function fetchProductExchange(system, { sessionId, productId, ...rest }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/product/${encodeURIComponent(productId)}/exchange?${queryString.stringify({ ...rest })}`, {
        method: "GET",
        headers: defaultHeaders,
    });
}

export function fetchSimilarProducts(system, { sessionId, productId, ...rest }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/products/${encodeURIComponent(productId)}/similar?${queryString.stringify({ ...rest })}`, {
        method: "GET",
        headers: defaultHeaders,
    });
}

export function fetchOrderStatus(system, { orderId, ...rest }) {
    return system.fetch(
        `${system.host}/api/v1/digitalreturn/status/${encodeURIComponent(orderId)}?${queryString.stringify({
            ...rest,
        })}`,
        {
            method: "GET",
            headers: defaultHeaders,
        },
    );
}

export function fetchPackingInstructions(system, { orderId, returnId, email, ...rest }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/packing-instructions/${encodeURIComponent(orderId)}`, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify({ buyerEmail: email, returnId, ...rest }),
    });
}

export function fetchUserComplaintAction(system, { foreignOrderId, returnId, email, brandName, ...rest }) {
    return system.fetch(
        `${system.host}/api/v1/digitalreturn/status/${encodeURIComponent(foreignOrderId)}/return/${encodeURIComponent(returnId)}/complaint?${queryString.stringify({
            email,
            brandName,
        })}`,
        {
            method: "POST",
            headers: defaultHeaders,
            body: JSON.stringify({ ...rest }),
        },
    );
}

export function fetchComplaintImageUrls(system, { sessionId, productId, ...rest }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/products/${encodeURIComponent(productId)}/image/prepare?${queryString.stringify({ ...rest })}`, {
        method: "GET",
        headers: defaultHeaders,
    });
}

export function fetchComplaintImagesUrls(system, { sessionId, productId, ...rest }) {
    return system.fetch(
        `${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/products/${encodeURIComponent(productId)}/complaint/images/prepare?${queryString.stringify({ ...rest })}`,
        {
            method: "POST",
            headers: defaultHeaders,
        },
    );
}

export function fetchRemoteComplaintImages(system, { sessionId, productId }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/products/${encodeURIComponent(productId)}/complaint/images`, {
        method: "GET",
        headers: defaultHeaders,
    });
}

export function fetchRefundCalculate(system, data) {
    const { sessionId, selectedProductsValues, shipmentCarrier, shipmentSolutionType } = data;
    return system.fetch(`${system.host}/api/v1/digitalreturn/session/${encodeURIComponent(sessionId)}/refund/calculate`, {
        method: "POST",
        body: JSON.stringify({
            products: selectedProductsValues,
            shipmentCarrier,
            shipmentSolutionType,
        }),
        mockData: data,
        headers: defaultHeaders,
    });
}

export function fetchExchangePayment(system, { orderToken, brandName, requestBody }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/exchange/payment/${encodeURIComponent(orderToken)}?${queryString.stringify({ brandName })}`, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: defaultHeaders,
    });
}

export function fetchExchangePaymentComplete(system, { orderToken, paymentMethodFields, brandName }) {
    return system.fetch(`${system.host}/api/v1/digitalreturn/exchange/payment/${encodeURIComponent(orderToken)}/complete?${queryString.stringify({ brandName })}`, {
        method: "POST",
        body: JSON.stringify({ paymentMethodFields }),
        headers: defaultHeaders,
    });
}

export function fetchDemoInit(system, { email }) {
    return system.fetch(`${system.host}/demo/init?${queryString.stringify({ email })}`, {
        method: "POST",
        headers: defaultHeaders,
    });
}

export function fetchDemoComplete(system, { name, email, uuid }) {
    return system.fetch(`${system.host}/demo/${encodeURIComponent(uuid)}/complete`, {
        method: "POST",
        body: JSON.stringify({ name, email }),
        headers: defaultHeaders,
    });
}

export function fetchChangePickupRequest(system, { brandName, pickupDate, foreignOrderId, returnId, email }) {
    return system.fetch(
        `${system.host}/api/v1/digitalreturn/status/${encodeURIComponent(foreignOrderId)}/return/${encodeURIComponent(returnId)}/pickup?${queryString.stringify({ brandName, email, date: pickupDate })}`,
        {
            method: "POST",
        },
    );
}

export function fetchStatusReturnDocuments(system, { foreignOrderId, returnId, brandName, email }) {
    return system.fetch(
        `${system.host}/api/v1/digitalreturn/status/${encodeURIComponent(foreignOrderId)}/return/${encodeURIComponent(returnId)}/documents?${queryString.stringify({ brandName, email })}`,
        {
            method: "GET",
            headers: defaultHeaders,
        },
    );
}
